'use client';
import { gsap } from 'gsap';
import { useRef, useEffect } from 'react';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContentButtons } from '@repo/ui/src/components/ToutContent';
import { ImageProps, MediaType, HeaderVariationProps } from '@repo/ui/src/types';
import { AlignH } from '@repo/ui/src/types/general';
import { AmplienceButtonProps } from '@repo/ui/src/types/button';
import { useMounted } from '@hooks/useMounted';
import { useIsMobile, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { classNames } from '@repo/ui/src/utils/classNames';

interface HomeHeroProps {
  lilHeading: string;
  headingLine1: string;
  headingLine2: string;
  headingLine3: string;
  headerVariation?: HeaderVariationProps;
  image?: ImageProps;
  buttons: Array<AmplienceButtonProps>;
  align: AlignH;
  backgroundImage: MediaType;
  _testID?: string;
}

const HomeHero = ({
  lilHeading,
  headingLine1,
  headingLine2,
  headingLine3,
  headerVariation,
  image,
  buttons,
  align,
  backgroundImage,
}: HomeHeroProps) => {
  const fullSize = headingLine1 && image?.image && buttons?.length;
  const ref = useRef<any>(null);
  const imageUrl = buildImageSrc(image, 500, 0, 0);
  const reduceMotion = useReducedMotion();
  const isMounted = useMounted();
  const xsOnly = useIsMobile();

  useEffect(() => {
    if (!isMounted) return;
    const element = ref?.current;
    const content = element?.querySelector('.hero-banner-content');
    const fade = element?.querySelector('.fade-container');
    const timeline = gsap.timeline();
    timeline
      .to(fade, {
        delay: 0.2,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
      })
      .fromTo(
        content,
        { translateY: reduceMotion ? '0%' : '7%' },
        { translateY: '0%', opacity: 1, duration: 1, ease: 'power3.out' },
      );
  }, [isMounted, reduceMotion]);

  const width = xsOnly ? 600 : 2000;
  let aspectRatioWidth: number, aspectRatioHeight: number;
  if (xsOnly && fullSize) {
    aspectRatioWidth = 9;
    aspectRatioHeight = 16;
  } else {
    aspectRatioWidth = 16;
    aspectRatioHeight = 9;
  }
  const backgroundImageUrl = backgroundImage
    ? buildImageSrc(backgroundImage, width, aspectRatioWidth, aspectRatioHeight)
    : null;

  const getAlignment = (align: AlignH) => {
    if (align === 'left') return 'tw-items-start tw-text-left';
    if (align === 'right') return 'tw-items-end tw-text-right';
    return 'tw-items-center tw-text-center';
  };
  const alignment = getAlignment(align);

  const HeadingElement = headerVariation?.headerVariation || 'h2';

  return (
    <section
      ref={ref}
      className={classNames(
        'tw-relative tw-overflow-hidden tw-w-full',
        'tw-bg-cover tw-bg-center',
        fullSize ? 'tw-h-screen' : '',
      )}
      style={{ backgroundImage: `url(${backgroundImageUrl})` }}
    >
      <div className="fade-container tw-absolute tw-inset-0 tw-bg-black tw-opacity-100 tw-z-0"></div>
      <div
        className={classNames(
          'tw-h-full tw-bg-auto tw-z-10 tw-relative',
          align === 'left' && 'tw-bg-gradient-to-l',
          align === 'right' && 'tw-bg-gradient-to-r',
          align === 'left' || align === 'right'
            ? 'tw-to-[rgba(0,0,0,0.5)_18%] tw-from-transparent'
            : 'tw-bg-[radial-gradient(circle,rgba(0,0,0,0.5)_18%,transparent_100%)]',
        )}
      >
        <div
          className={classNames(
            'hero-banner-content tw-opacity-0 tw-flex tw-flex-col tw-justify-center tw-h-full tw-p-[3vw]',
            alignment,
          )}
        >
          {(headingLine1 || headingLine2 || headingLine3) && (
            <HeadingElement className="tra-text-web-banner tw-relative tw-text-neutral-50 tw-mb-8 md:tw-mb-12 tw-text-[clamp(5rem,3.5rem+6vw,10rem)]">
              {lilHeading && (
                <span className="tw-block tw-text-[30%] tw-leading-[120%]">{lilHeading}</span>
              )}
              <span className="sr-only">&nbsp;</span>
              {headingLine1 && <span className="tw-block tw-mt-2">{headingLine1}</span>}
              <span className="sr-only">&nbsp;</span>
              {headingLine2 && <span className="tw-block">{headingLine2}</span>}
              <span className="sr-only">&nbsp;</span>
              <span className="tw-font-bold">{headingLine3}</span>
            </HeadingElement>
          )}

          {image?.image && (
            <div className="tw-mb-8">
              <img
                width="200"
                src={imageUrl}
                alt={image.image.name}
                className="tw-w-[58vw] md:tw-w-[18vw] portrait:md:tw-w-[36vw]"
              />
            </div>
          )}

          <ToutContentButtons
            buttons={buttons}
            align={align}
            buttonSize="large"
            buttonNegative
            dark
          />
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
